import { BOOL_FEATURE_FLAGS, FeatureFlagBool } from '../FeatureFlagBool';
import { FeatureFlagJson, JSON_FEATURE_FLAGS } from '../FeatureFlagJson';
import { FeatureFlagNumber, NUMBER_FEATURE_FLAGS } from '../FeatureFlagNumber';
import { FeatureFlagString, STRING_FEATURE_FLAGS } from '../FeatureFlagString';

export const FEATURE_FLAG_OVERRIDE_STORE_VERSION = 1;

export type FeatureFlagNames = FeatureFlagBool | FeatureFlagJson | FeatureFlagNumber | FeatureFlagString;

export type FeatureFlagOverrideSource = 'DEVTOOLS' | 'AGENT' | 'TEST' | 'PROFILE';

export type BooleanFeatureFlagOverride = {
  type: 'BOOLEAN';
  key: FeatureFlagBool;
  value: boolean;
};

export type JsonFeatureFlagOverride = {
  type: 'JSON';
  key: FeatureFlagJson;
  value: unknown;
};

export type NumberFeatureFlagOverride = {
  type: 'NUMBER';
  key: FeatureFlagNumber;
  value: number;
};

export type StringFeatureFlagOverride = {
  type: 'STRING';
  key: FeatureFlagString;
  value: string;
};

export type FeatureFlagOverride =
  | BooleanFeatureFlagOverride
  | JsonFeatureFlagOverride
  | NumberFeatureFlagOverride
  | StringFeatureFlagOverride;

/** Runtime kind discriminant shared by every override variant. */
export type FeatureFlagValueKind = FeatureFlagOverride['type'];

export type FeatureFlagOverrideEntry = {
  override: FeatureFlagOverride;
  source: FeatureFlagOverrideSource;
  reason?: string;
  createdAt: string;
  expiresAt?: string;
};

export type FeatureFlagOverrideStore = {
  version: typeof FEATURE_FLAG_OVERRIDE_STORE_VERSION;
  entries: FeatureFlagOverrideEntry[];
};

export type SetFeatureFlagOverrideInput = {
  key: FeatureFlagNames;
  type: FeatureFlagOverride['type'];
  value: unknown;
  source: FeatureFlagOverrideSource;
  reason?: string;
  ttlMinutes?: number;
};

const includesFeatureFlagName = (flagNames: readonly string[], value: string): boolean => flagNames.includes(value);

export const isFeatureFlagName = (value: string): value is FeatureFlagNames => {
  return (
    includesFeatureFlagName(BOOL_FEATURE_FLAGS, value) ||
    includesFeatureFlagName(JSON_FEATURE_FLAGS, value) ||
    includesFeatureFlagName(NUMBER_FEATURE_FLAGS, value) ||
    includesFeatureFlagName(STRING_FEATURE_FLAGS, value)
  );
};

export const isBooleanFeatureFlag = (value: string): value is FeatureFlagBool => {
  return includesFeatureFlagName(BOOL_FEATURE_FLAGS, value);
};

export const isJsonFeatureFlag = (value: string): value is FeatureFlagJson => {
  return includesFeatureFlagName(JSON_FEATURE_FLAGS, value);
};

export const isNumberFeatureFlag = (value: string): value is FeatureFlagNumber => {
  return includesFeatureFlagName(NUMBER_FEATURE_FLAGS, value);
};

export const isStringFeatureFlag = (value: string): value is FeatureFlagString => {
  return includesFeatureFlagName(STRING_FEATURE_FLAGS, value);
};
